import React, { useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Popover, Select } from "antd";
import { FilterOutlined } from "@ant-design/icons";

const POPOVER_Z = 10000;
const POPOVER_W = 280;
const SAFE_GUTTER = 12;

const TableFilter = React.memo(function TableFilter({
  label,
  kind = "checkbox", // "checkbox" | "select"
  value,
  onChange,
  options = [],

  listMaxHeight = 240,
  itemPaddingY = 6,
  itemFontSize = 16,
  controlHeight = 34,
  getPopupContainer,
  popupZIndex,
  popupMatchSelectWidth = true,

  // RDG header renderer props
  column,
  sortDirection,
  priority,
  onSort,
  tabIndex,
}) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState("bottom");
  const [q, setQ] = useState("");

  const popupContainer = getPopupContainer || (() => document.body);
  const popupZ = typeof popupZIndex === "number" ? popupZIndex : POPOVER_Z;

  const isActive =
    kind === "checkbox"
      ? Array.isArray(value) && value.length > 0
      : String(value || "").length > 0;

  const sortBadge = sortDirection ? (
    <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75 }}>
      {sortDirection === "ASC" ? "▲" : "▼"}
      {typeof priority === "number" ? ` ${priority + 1}` : ""}
    </span>
  ) : null;

  const computePlacement = () => {
    const el = triggerRef.current;
    if (!el) return "bottom";

    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || 0;

    if (r.left < POPOVER_W / 2 + SAFE_GUTTER) return "bottomLeft";
    if (vw - r.right < POPOVER_W / 2 + SAFE_GUTTER) return "bottomRight";
    return "bottom";
  };

  const filteredCheckboxOptions = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((x) => String(x).toLowerCase().includes(query));
  }, [options, q]);

  const selected = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleOne = (opt) => {
    const next = new Set(selectedSet);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };

  const checkboxContent = (
    <div style={{ width: POPOVER_W }}>
      <Input
        size="small"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div
        style={{ maxHeight: listMaxHeight, overflow: "auto", paddingRight: 6 }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          {filteredCheckboxOptions.map((opt) => (
            <Checkbox
              key={String(opt)}
              checked={selectedSet.has(opt)}
              onChange={() => toggleOne(opt)}
              style={{
                padding: `${itemPaddingY}px 0`,
                fontSize: itemFontSize,
                userSelect: "none",
              }}
            >
              {String(opt)}
            </Checkbox>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 10,
        }}
      >
        <Button size="small" onClick={() => onChange([])}>
          Clear
        </Button>
        <Button size="small" type="primary" onClick={() => onChange(options)}>
          Select all
        </Button>
      </div>
    </div>
  );

  return (
    <div
      ref={triggerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 6,
        padding: "6px 8px",
        boxSizing: "border-box",
      }}
    >
      {/* Sort label */}
      <button
        type="button"
        tabIndex={tabIndex}
        onClick={() => {
          if (!column?.sortable) return;
          onSort?.(column.key);
        }}
        style={{
          all: "unset",
          cursor: column?.sortable ? "pointer" : "default",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          lineHeight: "16px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {sortBadge}
      </button>

      {/* Filter control */}
      {kind === "checkbox" ? (
        <Popover
          open={open}
          onOpenChange={(nextOpen) => {
            if (nextOpen) setPlacement(computePlacement());
            setOpen(nextOpen);
          }}
          trigger="click"
          placement={placement}
          content={checkboxContent}
          getPopupContainer={popupContainer}
          zIndex={popupZ}
        >
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              height: controlHeight,
              borderRadius: 8,
              border: "1px solid #d9d9d9",
              padding: "0 10px",
              background: isActive ? "#e6f4ff" : "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selected.length ? `${selected.length} selected` : "All"}
            </span>
            <FilterOutlined style={{ opacity: isActive ? 1 : 0.6 }} />
          </button>
        </Popover>
      ) : kind === "select" ? (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Select
            size="small"
            value={value || ""}
            style={{ width: "100%" }}
            onChange={(v) => onChange(v)}
            options={[
              { label: "All", value: "" },
              ...options.map((x) => ({ label: String(x), value: x })),
            ]}
            getPopupContainer={popupContainer}
            popupMatchSelectWidth={popupMatchSelectWidth}
            styles={{ popup: { root: { zIndex: popupZ } } }}
          />
        </div>
      ) : null}
    </div>
  );
});

export default TableFilter;
